import { MOVED_SLUGS } from "@/content/moved-posts";
import { createServerFn } from "@tanstack/react-start";
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";
import { normalizeBlogSlug } from "./blog-slug";
import { removeEmDashesDeep as clean } from "./no-em-dash";

// ---- Public reads via server publishable client ---------------------------

function publicSupabase() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    {
      auth: {
        storage: undefined,
        persistSession: false,
        autoRefreshToken: false,
      },
    },
  );
}

export const listBlogPosts = createServerFn({ method: "GET" }).handler(
  async () => {
    const sb = publicSupabase();
    const { data, error } = await sb
      .from("blog_posts")
      .select(
        "slug, title, excerpt, hero_image_url, tags, published_at",
      )
      .eq("status", "published")
      .order("published_at", { ascending: false })
      .limit(60);
    if (error) throw error;
    return clean((data ?? []).filter((p) => !MOVED_SLUGS.has(p.slug)).slice(0, 50));
  },
);

export const getBlogPost = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) =>
    z.object({ slug: z.string().min(1) }).parse(input),
  )
  .handler(async ({ data }) => {
    const sb = publicSupabase();
    const { data: post, error } = await sb
      .from("blog_posts")
      .select("*")
      .eq("status", "published")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw error;
    if (post) return clean(post);

    // Slug changed since the URL was crawled/shared: resolve through the
    // post's recorded previous slugs so the route can 301 to the live URL
    // instead of 404-ing on a page that still exists.
    const { data: renamed, error: renamedError } = await sb
      .from("blog_posts")
      .select("*")
      .eq("status", "published")
      .contains("previous_slugs", [data.slug])
      .maybeSingle();
    if (renamedError) throw renamedError;
    return renamed ? clean(renamed) : renamed;
  });


export const listSitemapPosts = createServerFn({ method: "GET" }).handler(
  async () => {
    const sb = publicSupabase();
    const { data, error } = await sb
      .from("blog_posts")
      .select("slug, published_at, updated_at")
      .eq("status", "published");
    if (error) throw error;
    return (data ?? []).filter((p) => !MOVED_SLUGS.has(p.slug));
  },
);

// ---- Admin: list/edit/delete ---------------------------------------------

async function ensureAdmin(
  supabase: ReturnType<typeof publicSupabase>,
  userId: string,
) {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error("Forbidden");
}

export const adminListBlogPosts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context.supabase as never, context.userId);
    const { data, error } = await context.supabase
      .from("blog_posts")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw error;
    return data ?? [];
  });

const UpdateInput = z.object({
  id: z.string().uuid(),
  title: z.string().min(1).optional(),
  slug: z.string().transform(normalizeBlogSlug).pipe(z.string().regex(/^[a-zA-Z0-9]+(?:-[a-zA-Z0-9]+)*$/, "Enter the post name or its blog URL, without extra paths.")).optional(),
  excerpt: z.string().optional().nullable(),
  body_md: z.string().optional(),
  meta_title: z.string().optional().nullable(),
  meta_description: z.string().optional().nullable(),
  tags: z.array(z.string()).optional(),
  status: z.enum(["draft", "published"]).optional(),
  hero_image_url: z.string().url().optional().nullable(),
  hero_image_prompt: z.string().optional().nullable(),
  schema_jsonld: z.string().optional().nullable(),
});

export const updateBlogPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => UpdateInput.parse(input))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase as never, context.userId);

    const { id, ...rest } = data;
    const patch = { ...rest } as unknown as Database["public"]["Tables"]["blog_posts"]["Update"];
    let slugForPing: string | null = null;

    const { data: current } = await context.supabase
      .from("blog_posts")
      .select("published_at, slug, previous_slugs, tags, title")
      .eq("id", id)
      .maybeSingle();

    if (data.status === "published") {
      // Every published post must sit in a topic cluster (and so link to its
      // pillar). Unassigned posts stay drafts until a `cluster:<id>` tag is added.
      const { clusterForPost } = await import("./site-clusters");
      const slugNow = data.slug ?? current?.slug ?? "";
      if (!clusterForPost({ slug: slugNow, tags: data.tags ?? current?.tags ?? [] })) {
        throw new Error(
          "This post isn't assigned to a topic cluster yet. Add a tag like cluster:tracking, cluster:tools, cluster:how-ai-chooses, cluster:optimization or cluster:research, then publish.",
        );
      }
      if (!current?.published_at) {
        // New pages may not answer a question an existing page already answers.
        const { findQuestionConflict } = await import("./site-clusters");
        const conflict = findQuestionConflict(data.title ?? current?.title ?? "", `/blog/${slugNow}`);
        if (conflict) {
          throw new Error(
            `This post answers the same question as ${conflict.path} ("${conflict.label}"). Merge it into that page instead of publishing a new one.`,
          );
        }
        patch.published_at = new Date().toISOString();
      }
      slugForPing = data.slug ?? current?.slug ?? null;
    }

    // Renaming a post orphans every link that already points at the old URL.
    // Keep the old slug on the row so the blog route can 301 to the new one.
    if (data.slug && current?.slug && data.slug !== current.slug) {
      const kept = new Set([...(current.previous_slugs ?? []), current.slug]);
      kept.delete(data.slug);
      patch.previous_slugs = [...kept];
    }


    const { error } = await context.supabase
      .from("blog_posts")
      .update(patch)
      .eq("id", id);
    if (error) throw error;
    if (slugForPing) {
      const { pingIndexNow, blogPostUrl } = await import("./indexnow.server");
      await pingIndexNow([
        blogPostUrl(slugForPing),
        "https://www.monroya.ai/blog",
        "https://www.monroya.ai/sitemap.xml",
      ]);
    }
    return { ok: true };
  });

export const deleteBlogPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ id: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase as never, context.userId);
    // Fetch title first so we can permanently block autopilot from re-generating it.
    const { data: post } = await context.supabase
      .from("blog_posts")
      .select("title")
      .eq("id", data.id)
      .maybeSingle();
    const { error } = await context.supabase
      .from("blog_posts")
      .delete()
      .eq("id", data.id);
    if (error) throw error;
    if (post?.title) {
      // Ensure a settings row exists so deletion always blocks the title,
      // even on a fresh project where the autopilot table is empty.
      const { data: settings } = await context.supabase
        .from("blog_autopilot_settings")
        .select("id, blocked_topics")
        .limit(1)
        .maybeSingle();

      const title = post.title.trim();
      const existing = (settings?.blocked_topics ?? "").trim();
      const already = existing
        .split("\n")
        .map((l: string) => l.trim().toLowerCase())
        .includes(title.toLowerCase());

      const next = existing ? `${existing}\n${title}` : title;
      if (settings?.id) {
        if (!already) {
          const { error: updateError } = await context.supabase
            .from("blog_autopilot_settings")
            .update({ blocked_topics: next })
            .eq("id", settings.id);
          if (updateError) {
            console.error("failed to update blocked_topics", updateError);
            throw updateError;
          }
        }
      } else {
        const { error: insertError } = await context.supabase
          .from("blog_autopilot_settings")
          .insert({ id: true, blocked_topics: next });
        if (insertError) {
          console.error("failed to create autopilot settings row", insertError);
          throw insertError;
        }
      }
    }
    return { ok: true };
  });

// ---- Auto-generate a post -------------------------------------------------

const GenerateInput = z.object({
  topicHint: z.string().optional(),
  brief: z.string().optional(),
  publish: z.boolean().optional(),
});

export const generateBlogPostV4 = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => GenerateInput.parse(input ?? {}))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase as never, context.userId);
    const { generateAndInsertPost } = await import("./blog-generator.server");
    return generateAndInsertPost({
      topicHint: data.topicHint,
      brief: data.brief,
      publish: data.publish,
      authorId: context.userId,
      supabase: context.supabase as unknown as SupabaseClient<Database>,
    });
  });

export const generateBlogPostV3 = generateBlogPostV4;
export const generateBlogPostV2 = generateBlogPostV4;
export const generateBlogPost = generateBlogPostV4;

export const regenerateBlogHero = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ id: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase as never, context.userId);
    const { regenerateHeroForPost } = await import("./blog-generator.server");
    return regenerateHeroForPost(
      context.supabase as unknown as SupabaseClient<Database>,
      data.id,
    );
  });


// ---- Bottom-funnel link cluster (comparison / alternatives / pricing) -----

export const listComparisonPosts = createServerFn({ method: "GET" }).handler(
  async () => {
    const sb = publicSupabase();
    const { data, error } = await sb
      .from("blog_posts")
      .select("slug, title, tags, published_at")
      .eq("status", "published")
      .order("published_at", { ascending: false })
      .limit(200);
    if (error) throw error;
    const live = (data ?? []).filter((p) => !MOVED_SLUGS.has(p.slug));

    const TAGS = new Set([
      "comparison",
      "comparisons",
      "bottom-funnel",
      "alternatives",
      "pricing",
    ]);
    const PATTERN =
      /\b(vs\.?|versus|alternatives?|competitors?|cheapest|pricing|price|worth it|best|ranked|compare[d]?)\b/i;

    return live
      .filter((p) => {
        const tagged = (p.tags ?? []).some((t: string) =>
          TAGS.has(t.trim().toLowerCase()),
        );
        return tagged || PATTERN.test(p.title ?? "");
      })
      .slice(0, 12)
      .map((p) => clean({ slug: p.slug, title: p.title }));
  },
);

/**
 * Related posts for the end of an article. Tag overlap first, most recent
 * posts as the fallback, so every post carries at least three internal
 * links out to other indexable pages.
 */
export const listRelatedPosts = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) =>
    z.object({ slug: z.string(), tags: z.array(z.string()).default([]) }).parse(input),
  )
  .handler(async ({ data }) => {
    const sb = publicSupabase();
    const select = "slug, title, excerpt, published_at";
    let rows: Array<{ slug: string; title: string; excerpt: string | null }> = [];

    if (data.tags.length > 0) {
      const { data: tagged } = await sb
        .from("blog_posts")
        .select(select)
        .eq("status", "published")
        .overlaps("tags", data.tags)
        .neq("slug", data.slug)
        .order("published_at", { ascending: false })
        .limit(6);
      rows = (tagged ?? []) as typeof rows;
    }

    if (rows.length < 3) {
      const { data: recent } = await sb
        .from("blog_posts")
        .select(select)
        .eq("status", "published")
        .neq("slug", data.slug)
        .order("published_at", { ascending: false })
        .limit(8);
      for (const r of (recent ?? []) as typeof rows) {
        if (rows.length >= 3) break;
        if (!rows.some((x) => x.slug === r.slug)) rows.push(r);
      }
    }

    return clean(rows.slice(0, 3));
  });
